(() => {
  const controlGap = 12;
  const fixedPromptBottomPadding = 16;
  const transitionDuration = 460;
  const mobileViewport = window.matchMedia('(max-width: 1000px)');
  const arrowBottomByElement = new WeakMap();
  const chatBaseOffsetByElement = new WeakMap();
  const chatMetricsByElement = new WeakMap();
  let scheduledFrame = 0;

  const getNumber = (value, fallback = 0) => {
    const parsedValue = Number.parseFloat(value);

    return Number.isFinite(parsedValue) ? parsedValue : fallback;
  };

  const getActivePrompt = () => Array.from(document.querySelectorAll('[data-cws-home-reorder-prompt]')).find((prompt) => (
    !prompt.hidden && !prompt.classList.contains('is-hidden')
  ));

  const ensureChatActivatorLiftStyles = () => {
    if (document.querySelector('[data-cws-chat-activator-lift]')) {
      return;
    }

    const activatorLiftStyles = document.createElement('style');

    activatorLiftStyles.dataset.cwsChatActivatorLift = 'true';
    activatorLiftStyles.textContent = `
      /* Keep Shopify Inbox internals untouched while moving its exported activator. */
      shopify-chat::part(activator) {
        translate: 0 calc(-1 * var(--cws-chat-activator-lift, 0px)) !important;
      }
    `;
    document.head.append(activatorLiftStyles);
  };

  const updatePromptVisibility = (prompt) => {
    if (!prompt) {
      return;
    }

    const hero = document.querySelector('[data-cws-home-hero]');
    const heroActions = hero?.querySelector('.cws-home-hero__main .cws-home-hero__actions');
    const overlapTarget = mobileViewport.matches ? hero : heroActions;
    const promptInner = prompt.querySelector('.cws-home-reorder-prompt__inner');

    if (!overlapTarget || !promptInner) {
      prompt.classList.remove('is-hero-cta-overlap');
      prompt.removeAttribute('aria-hidden');

      if ('inert' in prompt) {
        prompt.inert = false;
      }

      return;
    }

    const overlapTargetRect = overlapTarget.getBoundingClientRect();
    const visualViewport = window.visualViewport;
    const viewportBottom = visualViewport
      ? visualViewport.offsetTop + visualViewport.height
      : window.innerHeight;
    const fixedPromptTop = viewportBottom - promptInner.offsetHeight - fixedPromptBottomPadding;
    const heroOverlapPrompt = overlapTargetRect.width > 0
      && overlapTargetRect.height > 0
      && overlapTargetRect.bottom + controlGap > fixedPromptTop
      && overlapTargetRect.top - controlGap < viewportBottom;

    prompt.classList.toggle('is-hero-cta-overlap', heroOverlapPrompt);
    prompt.setAttribute('aria-hidden', heroOverlapPrompt ? 'true' : 'false');

    if ('inert' in prompt) {
      prompt.inert = heroOverlapPrompt;
    }
  };

  const updateFloatingControls = (prompt) => {
    const arrow = document.querySelector('.scroll_top');
    const chat = document.querySelector('shopify-chat');

    if (!arrow || !chat) {
      return;
    }

    if (!arrowBottomByElement.has(arrow)) {
      arrowBottomByElement.set(arrow, getNumber(window.getComputedStyle(arrow).bottom, 40));
    }

    if (!chatBaseOffsetByElement.has(chat)) {
      chatBaseOffsetByElement.set(chat, getNumber(
        window.getComputedStyle(chat).getPropertyValue('--shopify-agent-activator-offset'),
        80,
      ));
    }

    const baseArrowBottom = arrowBottomByElement.get(arrow);
    const shopifyChatOffset = chatBaseOffsetByElement.get(chat);
    const arrowStyle = window.getComputedStyle(arrow);
    const arrowRect = arrow.getBoundingClientRect();
    const arrowHeight = getNumber(arrowStyle.height, window.innerWidth >= 768 ? 56 : 44);
    const arrowWidth = getNumber(arrowStyle.width, window.innerWidth >= 768 ? 56 : 44);
    const arrowRightOffset = getNumber(arrowStyle.right, 20);
    const arrowLeft = arrowRect.width ? arrowRect.left : window.innerWidth - arrowRightOffset - arrowWidth;
    const arrowRight = arrowLeft + arrowWidth;
    const chatActivator = chat.shadowRoot?.querySelector('[part="activator"]');
    const chatRect = chatActivator?.getBoundingClientRect();

    ensureChatActivatorLiftStyles();

    if (chatRect?.width && chatRect?.height) {
      chatMetricsByElement.set(chat, {
        height: chatRect.height,
        rightOffset: window.innerWidth - chatRect.right,
        width: chatRect.width,
      });
    }

    const chatMetrics = chatMetricsByElement.get(chat) ?? {
      height: 46,
      rightOffset: 23,
      width: 82,
    };
    const chatLeft = chatRect?.left ?? window.innerWidth - chatMetrics.rightOffset - chatMetrics.width;
    const chatRight = chatRect?.right ?? chatLeft + chatMetrics.width;
    const promptInner = prompt?.querySelector('.cws-home-reorder-prompt__inner');
    const promptRect = promptInner?.getBoundingClientRect();
    const controlLeft = Math.min(arrowLeft, chatLeft);
    const controlRight = Math.max(arrowRight, chatRight);
    const baseChatOffset = baseArrowBottom + arrowHeight + controlGap;
    const baseChatTop = window.innerHeight - baseChatOffset - chatMetrics.height;
    const baseArrowBottomEdge = window.innerHeight - baseArrowBottom;
    const promptIntersectsControlStack = promptRect
      && promptRect.right + controlGap > controlLeft
      && promptRect.left - controlGap < controlRight
      && promptRect.bottom + controlGap > baseChatTop
      && promptRect.top - controlGap < baseArrowBottomEdge;
    const arrowBottom = promptIntersectsControlStack
      ? Math.max(baseArrowBottom, window.innerHeight - promptRect.top + controlGap)
      : baseArrowBottom;
    const chatOffset = arrowBottom + arrowHeight + controlGap;
    const chatLift = Math.max(0, chatOffset - shopifyChatOffset);

    arrow.style.setProperty('bottom', `${Math.round(arrowBottom)}px`);
    chat.style.setProperty('--cws-chat-activator-lift', `${Math.round(chatLift)}px`);
  };

  const updateLayout = () => {
    scheduledFrame = 0;

    const prompt = getActivePrompt();

    updatePromptVisibility(prompt);
    updateFloatingControls(prompt?.classList.contains('is-hero-cta-overlap') ? null : prompt);
  };

  const scheduleLayoutUpdate = () => {
    if (scheduledFrame) {
      return;
    }

    scheduledFrame = window.requestAnimationFrame(updateLayout);
  };

  const initializePrompt = (prompt) => {
    if (!prompt || prompt.dataset.cwsHomeReorderPromptInitialized === 'true') {
      return;
    }

    const dismissButton = prompt.querySelector('[data-cws-home-reorder-dismiss]');

    if (!dismissButton) {
      return;
    }

    prompt.dataset.cwsHomeReorderPromptInitialized = 'true';

    dismissButton.addEventListener('click', () => {
      prompt.classList.add('is-hidden');
      prompt.setAttribute('aria-hidden', 'true');

      if ('inert' in prompt) {
        prompt.inert = true;
      }

      dismissButton.blur();
      scheduleLayoutUpdate();

      window.setTimeout(() => {
        if (document.body.contains(prompt)) {
          prompt.hidden = true;
          scheduleLayoutUpdate();
        }
      }, transitionDuration);
    });

    if ('ResizeObserver' in window) {
      const promptInner = prompt.querySelector('.cws-home-reorder-prompt__inner');

      if (promptInner) {
        new ResizeObserver(scheduleLayoutUpdate).observe(promptInner);
      }
    }
  };

  const initializePrompts = (scope = document) => {
    scope.querySelectorAll('[data-cws-home-reorder-prompt]').forEach(initializePrompt);
  };

  initializePrompts();
  scheduleLayoutUpdate();

  window.addEventListener('resize', scheduleLayoutUpdate, { passive: true });
  window.addEventListener('scroll', scheduleLayoutUpdate, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleLayoutUpdate, { passive: true });
  window.visualViewport?.addEventListener('scroll', scheduleLayoutUpdate, { passive: true });

  new MutationObserver(scheduleLayoutUpdate).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializePrompts(event.target);
    scheduleLayoutUpdate();
  });
})();
